import { api } from './api';

// ─── Assessment Config ─────────────────────────────────────────────────────────

export interface AssessmentConfigDTO {
  questionCount: number;
  timeLimitMinutes: number;
  totalMarks: number;
  source: string;
  isOverride: boolean;
  hasCourseConfig: boolean;
}

export interface AssessmentConfigRequest {
  questionCount: number;
  timeLimitMinutes: number;
  totalMarks: number;
}

// ─── Assessment Questions ──────────────────────────────────────────────────────

export interface AssessmentQuestionDTO {
  id: number;
  questionText: string;
  modelAnswer: string;
  allocatedMarks: number;
  status: string;
  source: string;
  displayOrder: number;
}

export interface AssessmentQuestionRequest {
  questionText: string;
  modelAnswer: string;
  allocatedMarks: number;
}

// ─── Submissions ───────────────────────────────────────────────────────────────

export interface SubmissionSummaryDTO {
  submissionId: number;
  studentId: number;
  studentName: string;
  status: string;
  fileCount: number;
  updatedAt: string;
}

export interface SubmissionFileDTO {
  id: number;
  originalFilename: string;
  contentType: string;
  fileSizeBytes: number;
  presignedUrl: string;
  uploadedAt: string;
}

export interface SubmissionEvaluationDTO {
  feedbackText?: string;
  mark: number;
  totalMarks: number;
  evaluatedAt: string;
}

export interface SubmissionDetailDTO {
  submissionId: number;
  studentId: number;
  studentName: string;
  status: string;
  files: SubmissionFileDTO[];
  evaluation?: SubmissionEvaluationDTO;
  questions: { questionText: string; modelAnswer: string; allocatedMarks: number }[];
  totalMarks: number;
  updatedAt: string;
}

export interface EvaluationRequest {
  feedbackText?: string;
  mark: number;
}

// ─── Past Papers ───────────────────────────────────────────────────────────────

export interface PastPaperDTO {
  id: number;
  questionText: string;
  modelAnswer?: string;
  yearLabel?: string;
  sourceExam?: string;
  allocatedMarks?: number;
  status: string;
  source: string;
  displayOrder: number;
}

export interface PastPaperRequest {
  questionText: string;
  modelAnswer?: string;
  yearLabel?: string;
  sourceExam?: string;
  allocatedMarks?: number;
}

export interface StudentPastPaperDTO {
  id: number;
  questionText: string;
  yearLabel?: string;
  sourceExam?: string;
  allocatedMarks?: number;
  displayOrder: number;
}

// ─── API Calls ─────────────────────────────────────────────────────────────────

const base = (courseId: number, nodeId: number) =>
  `/api/tutor/courses/${courseId}/topics/${nodeId}`;

export async function getNodeAssessmentConfig(courseId: number, nodeId: number): Promise<AssessmentConfigDTO> {
  const res = await api.get(`${base(courseId, nodeId)}/assessment-config`);
  return res.data.data;
}

export async function saveNodeAssessmentConfig(courseId: number, nodeId: number, req: AssessmentConfigRequest): Promise<AssessmentConfigDTO> {
  const res = await api.put(`${base(courseId, nodeId)}/assessment-config`, req);
  return res.data.data;
}

export async function deleteNodeAssessmentConfig(courseId: number, nodeId: number): Promise<AssessmentConfigDTO> {
  const res = await api.delete(`${base(courseId, nodeId)}/assessment-config`);
  return res.data.data;
}

export async function getPublishedQuestions(courseId: number, nodeId: number): Promise<AssessmentQuestionDTO[]> {
  const res = await api.get(`${base(courseId, nodeId)}/assessment-questions`);
  return res.data.data;
}

export async function getPendingQuestions(courseId: number, nodeId: number): Promise<AssessmentQuestionDTO[]> {
  const res = await api.get(`${base(courseId, nodeId)}/assessment-questions/pending`);
  return res.data.data;
}

export async function addManualQuestion(courseId: number, nodeId: number, req: AssessmentQuestionRequest): Promise<AssessmentQuestionDTO> {
  const res = await api.post(`${base(courseId, nodeId)}/assessment-questions`, req);
  return res.data.data;
}

export async function generateQuestions(courseId: number, nodeId: number, countOverride?: number): Promise<AssessmentQuestionDTO[]> {
  const res = await api.post(`${base(courseId, nodeId)}/assessment-questions/generate`,
    countOverride ? { questionCountOverride: countOverride } : {});
  return res.data.data;
}

export async function acceptQuestion(courseId: number, nodeId: number, questionId: number): Promise<AssessmentQuestionDTO> {
  const res = await api.post(`${base(courseId, nodeId)}/assessment-questions/${questionId}/accept`);
  return res.data.data;
}

export async function deleteQuestion(courseId: number, nodeId: number, questionId: number): Promise<void> {
  await api.delete(`${base(courseId, nodeId)}/assessment-questions/${questionId}`);
}

export async function listSubmissions(courseId: number, nodeId: number): Promise<SubmissionSummaryDTO[]> {
  const res = await api.get(`${base(courseId, nodeId)}/assessment/submissions`);
  return res.data.data;
}

export async function getSubmissionDetail(courseId: number, nodeId: number, submissionId: number): Promise<SubmissionDetailDTO> {
  const res = await api.get(`${base(courseId, nodeId)}/assessment/submissions/${submissionId}`);
  return res.data.data;
}

export async function submitEvaluation(courseId: number, nodeId: number, submissionId: number, req: EvaluationRequest): Promise<void> {
  await api.put(`${base(courseId, nodeId)}/assessment/submissions/${submissionId}/evaluation`, req);
}

export async function getPastPapers(courseId: number, nodeId: number): Promise<PastPaperDTO[]> {
  const res = await api.get(`${base(courseId, nodeId)}/past-papers`);
  return res.data.data;
}

export async function addPastPaper(courseId: number, nodeId: number, req: PastPaperRequest): Promise<PastPaperDTO> {
  const res = await api.post(`${base(courseId, nodeId)}/past-papers`, req);
  return res.data.data;
}

export async function generatePastPapers(courseId: number, nodeId: number, countOverride?: number): Promise<PastPaperDTO[]> {
  const res = await api.post(`${base(courseId, nodeId)}/past-papers/generate`,
    countOverride ? { questionCountOverride: countOverride } : {});
  return res.data.data;
}

export async function acceptPastPaper(courseId: number, nodeId: number, questionId: number): Promise<PastPaperDTO> {
  const res = await api.post(`${base(courseId, nodeId)}/past-papers/${questionId}/accept`);
  return res.data.data;
}

export async function deletePastPaper(courseId: number, nodeId: number, questionId: number): Promise<void> {
  await api.delete(`${base(courseId, nodeId)}/past-papers/${questionId}`);
}
